import { ACPVariant } from 'apps/acp/variants/acp-variant';

const colors = {
  neutral: {
    color50: '#F5F5F5',
    color100: '#EAEBEB',
    color200: '#D5D6D7',
    color300: '#C1C2C3',
    color400: '#ACADAF',
    color500: '#97999B',
    color600: '#797A7C',
    color700: '#5B5C5D',
    color800: '#3C3D3E',
    color900: '#1E1F1F'
  },
  default: {
    color50: '#FFE6D5',
    color100: '#FFCDAA',
    color200: '#FFB480',
    color300: '#FF9B55',
    color400: '#FF822B',
    color500: '#FF6900',
    color600: '#CC5400',
    color700: '#993F00',
    color800: '#662A00',
    color900: '#331500'
  },
  accent: {
    color50: '#FFFAD5',
    color100: '#FEF6AA',
    color200: '#FEF180',
    color300: '#FDEC55',
    color400: '#FDE82B',
    color500: '#FCE300',
    color600: '#CAB600',
    color700: '#978800',
    color800: '#655B00',
    color900: '#322D00'
  },
  positive: {
    color50: '#F3F9E6',
    color100: '#E6F2CC',
    color200: '#CEE599',
    color300: '#B5D766',
    color400: '#9DCA33',
    color500: '#84BD00',
    color600: '#6A9700',
    color700: '#4F7100',
    color800: '#354C00',
    color900: '#1A2600'
  },
  negative: {
    color50: '#F8DBDD',
    color100: '#F0B7BA',
    color200: '#E99398',
    color300: '#E16F75',
    color400: '#DA4B53',
    color500: '#D22730',
    color600: '#A81F26',
    color700: '#7E171D',
    color800: '#541013',
    color900: '#2A080A'
  },
  special: {
    color50: '#D5D5D5',
    color100: '#AAAAAA',
    color200: '#808080',
    color300: '#555555',
    color400: '#2B2B2B',
    color500: '#000000',
    color600: '#000000',
    color700: '#000000',
    color800: '#000000',
    color900: '#000000'
  }
};

const variant: ACPVariant = {
  id: 'www.playcard.com',
  productTitle: 'PlayCard',
  productType: 'gpr',
  accessDomain: {
    subdomain: 'www',
    domain: 'playcard',
    topLevelDomain: 'com'
  },
  colors,
  themeConfiguration: {
    acpRisingTideLayout: {
      notificationCounterBackgroundColor: colors.default.color500,
      appBarDefaultBackgroundColor: colors.special.color500
    },
    illustration: {
      default: colors.special,
      special: colors.default
    },
    acpActivationAppBar: {
      defaultBackgroundColor: colors.special.color500
    }
  },
  legacyAcpTheme: 'playcard',
  legacyAcpBrandName: 'playcard',
  legacyLegosTheme: 'playcard',
  status_bar_background_color: colors.special.color500,
  status_bar_foreground_color: 'dark',
  contactInfo: {
    name: 'PlayCard',
    contact_address_line1: 'PO Box 2136',
    contact_address_line2: 'Austin, TX 78768-2136',
    contact_address_line3: '',
    customer_support_email: 'customerservice@playcard.com',
    fax_formatted: '(866) 358-0526',
    phone_formatted: '(800) 269-0185',
    phone: '8002690185',
    phone_international: '737-220-8956',
    balance_check_phone: '8666476929',
    balance_check_phone_formatted: '1-866-647-6929',
    pre_funded_checks_phone: '18778147683',
    pre_funded_checks_phone_formatted: '1-877-814-7683'
  },
  configuration: {
    apiKeys: {
      appsFlyerDevKey: '5Us7NeWk6WVjGLsAnKioSk',
      appsFlyerBannerKey: '3442969f-db79-4c09-85c6-e04d920d76db',
      'google-tag-manager-container-id': 'GTM-NQXKPD',
      'google-tag-manager-container-id-mobile': 'GTM-MWBTT5M',
      paypalFraudNetPayerIdProd: '',
      paypalFraudNetPayerIdTest: ''
    },
    featureToggles: {
      activationSetExternalId: false,
      appsFlyerEnabled: false,
      combinedTransactionsMasterAndSub: false,
      'embedded-activation': true,
      embeddedPayNearMe: false,
      hasNotificationCenter: false,
      mobilePBREnabled: true,
      preOnboardingEnabled: false,
      showAccountDisplayNickname: false,
      showAcquisitionLink: true,
      showATMFriendlyCards: true,
      showDisplayNameForSubAccount: false,
      showEmailInformationButton: true,
      showFeePlanPage: false,
      showHowToEarnRewardPointLink: true,
      showRegisterLink: true,
      showScanCardRegistration: true,
      showMarketingSiteLink: true,
      showFreeAtmFinderLink: false,
      showWUTransfers: true,
      showReloadLocationLink: true
    },
    misc: {
      distributor: 'playcard',
      layoutComponentOverride: false,
      loader: 'generic',
      payNearMeButtonTextColor: '#FFFFFF',
      payNearMeActionBarBackgroundColor: colors.default.color500,
      payNearMeTitleTextColor: '#FFFFFF',
      payNearMeColorAccent: colors.accent.color500,
      marketingSiteUrl: 'https://www.playcard.com',
      marketingSiteLinkText: 'Playcard.com',
      locationSearchMax: '25',
      iOSAppId: '1543347557'
    },
    content: {
      openAccountButtonContent: 'Order a New Card',
      signUpContent: "Don't have a card? Sign up today.",
      accountTermsLinkText: 'Cardholder Agreement',
      patriotActDisclosure: `1 <strong>IMPORTANT INFORMATION FOR OPENING A CARD ACCOUNT:
      </strong> To help the federal government fight the funding of terrorism and money
      laundering activities, the USA PATRIOT Act requires us to obtain, verify, and record
      information that identifies each person who opens a Card Account.
      <strong>WHAT THIS MEANS FOR YOU:</strong> When you open a Card Account, we will ask for
      your <strong>name, address, date of birth, and your government ID number.</strong>
      We may also ask to see your driver's license or other identifying information.
      Card activation and identity verification required before you can use the Card Account.
      If your identity is partially verified, full use of the Card Account will be restricted,
      but you may be able to use the Card for in-store purchase transactions.
      Restrictions include: no ATM withdrawals, international transactions, account-to-account
      transfers and additional loads. Use of Card Account also subject to fraud prevention
      restrictions at any time, with or without notice. Residents of Vermont are ineligible
      to open a Card Account.`
    },
    disclaimers: {},
    sections: [
      'dashboard-gpr',
      'dashboard-lite',
      'dashboard-handoff',
      'layout',
      'manage-gpr',
      'move-money',
      'overdraft',
      'locations',
      'direct-deposit',
      'direct-deposit-mobile-panel',
      'statements',
      'contact-us',
      'restrictions-readonly',
      'feeplan',
      'western-union',
      'disclosure-dictionary',
      'check-deposit',
      'checks',
      'quickbooks-oauth',
      'points',
      'post-handoff-to-activate-register',
      'upgrade',
      'offers',
      'offers-intro',
      'tours',
      'post-install-multi-screen-acquisition-flow',
      'echecks',
      'external-loyalty',
      'secure-inbox',
      'benefit-center',
      'atm-finder',
      'savings',
      'pay-bills',
      'premier-plan',
      'activation',
      'savings-transfer',
      'anytime-alerts',
      'anytime-alerts-upgrade',
      'change-username',
      'change-password',
      'mobile-check-load',
      'feedback',
      'rent-track',
      'direct-deposit-form',
      'dynamic-faq',
      'billpay',
      'card',
      'refer-a-friend',
      'close-account',
      'points-learn-more',
      'family-cards',
      'bank-transfers',
      'handoff',
      'debit-card-transfers',
      'card-use-settings',
      'secure-upload',
      'digital-wallet',
      'me-to-me-transfer',
      'money-gram',
      'survey',
      'dda-activation',
      'debit-card-transfers-disbursement',
      'spend-money',
      'fraud-detection',
      'spending-tracker',
      'connected-banks',
      'interstitials',
      'dd-tracker'
    ]
  }
};

export default variant;
